import {
  useCallback,
  ChangeEvent,
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react'

import CloseIcon from 'shared/icons/CloseIcon'
import MagnifyIcon from 'shared/icons/MagnifyIcon'
import debounce from 'lodash.debounce'
import cn from 'classnames'

type SearchBarProps = {
  defaultValue?: string
  onChange?: (value: string) => void
  placeholder?: string
  onBlur?: () => void
  onFocus?: () => void
  noBorder?: boolean
  classNames?: string
}

export type SearchBarRef = {
  setInputText: (value: string) => void
}

const SearchBar: ForwardRefRenderFunction<SearchBarRef, SearchBarProps> = (props, ref) => {
  const { onChange, placeholder, defaultValue = '', onBlur, onFocus, noBorder, classNames } = props
  const inputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState(defaultValue)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onInputChange = useCallback(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setText(value)
      typeof onChange === 'function' && onChange(value)
    }, 700),
    [onChange],
  )

  const onClearButtonClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }

    setText('')
    typeof onChange === 'function' && onChange('')
  }, [onChange])

  const handleOnFocus = useCallback(() => {
    onFocus && onFocus()
    inputRef.current?.focus()
  }, [onFocus])

  // reset the input value when defaultValue changes
  useEffect(() => {
    setText(defaultValue)
    inputRef.current && (inputRef.current.value = defaultValue)
    typeof onChange === 'function' && onChange(defaultValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue])

  useImperativeHandle(ref, () => ({
    setInputText: (value: string) => {
      setText(value)
      inputRef.current && (inputRef.current.value = value)
      typeof onChange === 'function' && onChange(value)
    },
  }))

  return (
    <div
      className={cn(
        'inline-flex w-full items-center gap-2 bg-[#FDFDFD] px-3',
        noBorder ? '' : 'rounded-full border border-black/10 ',
      )}
      // onClick={() => inputRef.current?.focus()}
    >
      <MagnifyIcon className="size-6 lg:size-8" />
      <input
        defaultValue={defaultValue}
        onChange={onInputChange}
        className={cn(
          'w-full border-none bg-[#FDFDFD] px-0 text-xs',
          'placeholder:text-[10px] focus:outline-0 focus:ring-0  lg:text-base placeholder:lg:text-base',
          classNames,
        )}
        ref={inputRef}
        placeholder={placeholder}
        onFocus={handleOnFocus}
        onBlur={onBlur}
      />
      {text && (
        <button onClick={onClearButtonClick}>
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

export default forwardRef(SearchBar)
